import { createSlice, createSelector, createEntityAdapter, createAsyncThunk, createAction } from "@reduxjs/toolkit";
import { simpleSetPayload, TREATMENT_EVENT_SOURCES, DEVICE_TYPE_DICT, BROWSER_TYPE_DICT, OS_NAME_DICT } from "../constants";
import { sortByFieldAlphabetically } from "@amplio-common/funcs/commonFunctionalities";
import { initErrorList, normalizeTechStats, getUserInitErrorList } from "@amplio-common/funcs/techStatusFunc";
import { getUploadSpeed, getDownloadSpeed, getJSNumOperations, getJSResolveTime } from "@amplio-common/funcs/performanceTests";
import {
  osVersion,
  browserVersion,
  browserName,
  osName,
  deviceType,
  fullBrowserVersion,
} from "react-device-detect";
import { createTreatmentMetaDataPromise, createTreatmentEventPromise, createTreatmentMultipleEventPromise } from "@amplio-common/treatments/api";
import { SLICE_NAMES } from "./sliceNames";

export const hiFromServerTime = createAction("techStatusSlice/hiFromServerTime");
export const assignActivity = createAction("techStatusSlice/assignActivity");
export const receviedActivity = createAction("techStatusSlice/receviedActivity");


const techStatusSlice_STATE_ROOT_SELECTOR = state => state[SLICE_NAMES.TECH_STATUS_SLICE];

const createSimpleSelector = (fieldName) => createSelector(
  [state => state[SLICE_NAMES.TECH_STATUS_SLICE][fieldName]], field => field,
);

const sortFunc = sortByFieldAlphabetically("name");

export const techStatusStudentsAdapter = createEntityAdapter({
  sortComparer: sortFunc,
});

export const createTreatmentEvent = createAsyncThunk(
  `${SLICE_NAMES.TECH_STATUS_SLICE}/createTreatmentEvent`,
  async ({ treatmentId, eventSource, eventName, eventValue, eventProps, relevantToStudent }) => {
    if (!treatmentId) { return; }
    const data = {
      source: eventSource || TREATMENT_EVENT_SOURCES.FRONT,
      event_name: eventName,
      event_value: eventValue,
      event_props: JSON.stringify(eventProps),
      relevant_to_student: relevantToStudent,
    };
    createTreatmentEventPromise(treatmentId, data);
  },
);

export const createTreatmentEventMany = createAsyncThunk(
  `${SLICE_NAMES.TECH_STATUS_SLICE}/createTreatmentEventMany`,
  async ({ treatmentId, eventSource, eventNameList, eventValue, eventProps, relevantToStudent }) => {
    if (!treatmentId) { return; }
    const resList = [];
    eventNameList.forEach(eName => {
      resList.push({
        source: eventSource || TREATMENT_EVENT_SOURCES.FRONT,
        event_name: eName,
        event_value: eventValue,
        event_props: JSON.stringify(eventProps),
        relevant_to_student: relevantToStudent,
      });
    });

    createTreatmentMultipleEventPromise(treatmentId, resList);
  },
);

export const techStatusMapTSToStudentStatus = (ts) => ({
  id: ts.student.id,
  name: ts.student.first_name,
  browserName: null,
  browserVersion: null,
  fullBrowserVersion: null,
  deviceType: null,
  downloadSpeed: null,
  uploadSpeed: null,
  osVersion: null,
  osName: null,
  isTabVisible: true,
  isWindowFocused: false,
  isVideoPermissionGranted: null,
  isAudioPermissionGranted: null,
  isWeakStreamConnection: null,
  jsResolveMs: null,
  jsNumOps: null,
  errorList: initErrorList,
});

const techStatusStudentsAdapterSelectors = techStatusStudentsAdapter.getSelectors(techStatusSlice_STATE_ROOT_SELECTOR);

const initialState = {
  browserName: null,
  browserVersion: null,
  fullBrowserVersion: null,
  deviceType: null,
  downloadSpeed: null,
  uploadSpeed: null,
  isTabVisible: true,
  isWindowFocused: false,
  isVideoPermissionGranted: null,
  isAudioPermissionGranted: null,
  isWeakStreamConnection: null,
  osVersion: null,
  osName: null,
  jsResolveMs: null,
  jsNumOps: null,
  isErrors: 0,
  errorList: initErrorList,
  hiToServerTime: null,
  ...techStatusStudentsAdapter.getInitialState(),
};

export const techStatusSlice = createSlice({
  name: SLICE_NAMES.TECH_STATUS_SLICE,
  initialState,
  reducers: {
    setInitialTechStatus: (state, action) => ({ ...state,
      ...action?.payload }),
    setBrowserName: simpleSetPayload("browserName"),
    setBrowserVersion: simpleSetPayload("browserVersion"),
    setDeviceType: simpleSetPayload("deviceType"),
    setDownloadSpeed: simpleSetPayload("downloadSpeed"),
    setUploadSpeed: simpleSetPayload("uploadSpeed"),
    setIsAudioPermissionGranted: simpleSetPayload("isAudioPermissionGranted"),
    setIsVideoPermissionGranted: simpleSetPayload("isVideoPermissionGranted"),
    setIsTabVisible: simpleSetPayload("isTabVisible"),
    setIsWindowFocused: simpleSetPayload("isWindowFocused"),
    setIsWeakStreamConnection: simpleSetPayload("isWeakStreamConnection"),
    setJsResolveMs: simpleSetPayload("jsResolveMs"),
    setJsNumOps: simpleSetPayload("jsNumOps"),
    setErrorList: simpleSetPayload("errorList"),
    setHiToServerTime: simpleSetPayload("hiToServerTime"),
    
    techStatusUpdateStudentStatus: techStatusStudentsAdapter.updateOne,
    techStatusSetStudentsStatus: techStatusStudentsAdapter.setAll,
    techStatusUpdateAllStudents: techStatusStudentsAdapter.updateMany,
  },
  extraReducers: {
  },
});

export default techStatusSlice.reducer;

//actions
export const { techStatusUpdateStudentStatus, techStatusSetStudentsStatus, techStatusUpdateAllStudents,
  setInitialTechStatus, setBrowserName, setBrowserVersion, setDeviceType, setDownloadSpeed, setUploadSpeed,
  setIsAudioPermissionGranted, setIsVideoPermissionGranted, setIsWindowFocused, setIsWeakStreamConnection, setIsTabVisible, setHiToServerTime,
  setJsResolveMs, setJsNumOps, setInitErrorList, setPermissionErrorList, setFocusErrorList, setConnectivityErrorList, setErrorList } = techStatusSlice.actions;

export const updateInitialTechStatus = createAsyncThunk(
  `${SLICE_NAMES.TECH_STATUS_SLICE}/getInitialTechStatus`,
  async ({ treatmentId }, thunkAPI) => {
    const { dispatch } = thunkAPI;
        
    const uploadSpeed = await getUploadSpeed();
    const downloadSpeed = await getDownloadSpeed();
    const jsResolveMs = await getJSResolveTime();
    const jsNumOps = getJSNumOperations();
    const deviceNetworkDetails = {
      osVersion,
      browserVersion,
      fullBrowserVersion,
      uploadSpeed,
      downloadSpeed,
      deviceType: DEVICE_TYPE_DICT[deviceType],
      browserName: BROWSER_TYPE_DICT[browserName],
      osName: OS_NAME_DICT[osName],
      jsResolveMs,
      jsNumOps,
      isTabVisible: true,
    };
    dispatch(setInitialTechStatus(deviceNetworkDetails));

    const initErrObj = getUserInitErrorList(deviceNetworkDetails);
    dispatch(setErrorList(initErrObj));

    const snakeCasedData = normalizeTechStats(deviceNetworkDetails);
    treatmentId && window.APP_FEATURE_FLAGS.METRIC_CLIENT_LOGS && createTreatmentMetaDataPromise(treatmentId, snakeCasedData);
  },
);

//selectors
export const selectBrowserName = createSimpleSelector("browserName");
export const selectBrowserVersion = createSimpleSelector("browserVersion");
export const selectOsName = createSimpleSelector("osName");
export const selectOsVersion = createSimpleSelector("osVersion");
export const selectDeviceType = createSimpleSelector("deviceType");
export const selectDownloadSpeed = createSimpleSelector("downloadSpeed");
export const selectUploadSpeed = createSimpleSelector("uploadSpeed");
export const selectIsVideoPermissionGranted = createSimpleSelector("isVideoPermissionGranted");
export const selectIsAudioPermissionGranted = createSimpleSelector("isAudioPermissionGranted");
export const selectIsTabVisible = createSimpleSelector("isTabVisible");
export const selectIsWindowFocused = createSimpleSelector("isWindowFocused");
export const selectIsWeakStreamConnection = createSimpleSelector("isWeakStreamConnection");
export const selectJsResolveMs = createSimpleSelector("jsResolveMs");
export const selectJsNumOps = createSimpleSelector("jsNumOps");
export const selectInitErrorList = createSimpleSelector("initErrorList");
export const selectPermissionErrorList = createSimpleSelector("permissionErrorList");
export const selectFocusErrorList = createSimpleSelector("focusErrorList");
export const selectConnectivityErrorList = createSimpleSelector("connectivityErrorList");
export const selectErrorList = createSimpleSelector("errorList");
export const selectHiToServerTime = createSimpleSelector("hiToServerTime");
export const selectFullBrowserVersion = createSimpleSelector("fullBrowserVersion");

export const selectTechStatus = createSelector([
  selectBrowserName, selectBrowserVersion, selectFullBrowserVersion, selectDeviceType, selectOsName, selectOsVersion,
  selectDownloadSpeed, selectUploadSpeed, selectIsVideoPermissionGranted,
  selectIsAudioPermissionGranted, selectIsWindowFocused, selectJsResolveMs, selectJsNumOps, selectErrorList], (
  brsName, brsVer, brsFullVer, dvcType, os, osVer, downloadSpeed, uploadSpeed, isVideoPermissionGranted,
  isAudioPermissionGranted, isWindowFocused, jsResolveMs, jsNumOps, errorList,
) => ({
  browserName: brsName,
  browserVersion: brsVer,
  fullBrowserVersion: brsFullVer,
  deviceType: dvcType,
  downloadSpeed,
  uploadSpeed,
  isTabVisible: true,
  isWindowFocused,
  isVideoPermissionGranted,
  isAudioPermissionGranted,
  osVersion: osVer,
  osName: os,
  jsResolveMs,
  jsNumOps,
  errorList,
}));

export const { selectAll: techStatusSelectAllStudentsStatus, selectIds: techStatusSelectStudentStatusIds,
  selectById: techStatusSelectStudentStatusById, selectEntities: techStatusSelectAllStudentStatusDict } = techStatusStudentsAdapterSelectors;
